import React from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { formatInsightMonthYearSlash } from "../../utils/rechargeSalesInsights";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const CHART_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
];

const COLOR_ABOVE_AVG = "#3b4fc8"; // blue — above 12-week average (per reference image)
const COLOR_BELOW_AVG = "#f87171"; // orange-red — below 12-week average

export const INSIGHTS_MTD_COLOR = COLOR_ABOVE_AVG;
export const INSIGHTS_LMTD_COLOR = COLOR_BELOW_AVG;

/**
 * Bar chart for weekly trends (Last 12 weeks).
 * Bars are color-coded: blue when >= weeklyAvg, orange-red when below.
 * weeklyAvg comes from the weekly-trends API response.
 */
export function InsightsTrendBarChart({
  labels = [],
  values = [],
  datasetLabel = "Value",
  weeklyAvg = 0,
}) {
  if (!labels.length || !values.length) return null;

  const avg = Number(weeklyAvg) || 0;
  const barColors = values.map((v) =>
    Number(v) >= avg ? COLOR_ABOVE_AVG : COLOR_BELOW_AVG
  );

  // Use W1, W2 … W12 for x-axis; keep raw dates for tooltip
  const weekLabels = labels.map((_, i) => `W${i + 1}`);

  /** Format "dd-mm-yyyy" → "dd MMM" e.g. "23 Mar" */
  const formatWeekDate = (raw) => {
    if (!raw) return "";
    const parts = String(raw).split("-");
    if (parts.length < 3) return raw;
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const m = parseInt(parts[1], 10) - 1;
    return `${parseInt(parts[0], 10)} ${monthNames[m] || ""}`;
  };

  const data = {
    labels: weekLabels,
    datasets: [
      {
        label: datasetLabel,
        data: values,
        backgroundColor: barColors,
        borderColor: barColors,
        borderWidth: 0,
        borderRadius: { topLeft: 6, topRight: 6 },
        borderSkipped: "bottom",
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false },
      legend: {
        display: true,
        labels: {
          generateLabels: () => [
            { text: "Weekly total", fillStyle: COLOR_ABOVE_AVG, strokeStyle: COLOR_ABOVE_AVG, lineWidth: 1, hidden: false },
            { text: `Below avg (${avg.toLocaleString("en-IN", { maximumFractionDigits: 0 })})`, fillStyle: COLOR_BELOW_AVG, strokeStyle: COLOR_BELOW_AVG, lineWidth: 1, hidden: false },
          ],
        },
      },
      title: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (!items.length) return "";
            const i = items[0].dataIndex;
            const weekDate = formatWeekDate(labels[i]);
            return `W${i + 1}  (w/c ${weekDate})`;
          },
          label: (ctx) => {
            const v = Number(ctx.raw);
            const formatted = v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
            const diff = v - avg;
            const sign = diff >= 0 ? "+" : "";
            return ` ${formatted}  (${sign}${Math.round(diff).toLocaleString("en-IN")} vs avg)`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: 11 }, maxRotation: 0 },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          beginAtZero: true,
          callback: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v,
        },
      },
    },
  };
  return <Bar options={options} data={data} />;
}

/**
 * Donut chart for monthly trends (Last 3 months).
 * Percentage labels shown inside each segment.
 * Legend at bottom shows Month/Year + percentage.
 */
export function InsightsTrendPieChart({
  labels = [],
  values = [],
  datasetLabel = "Value",
}) {
  if (!labels.length || !values.length) return null;
  const colors = CHART_COLORS.slice(0, labels.length);
  const total = values.reduce((s, v) => s + Number(v), 0);

  const data = {
    labels,
    datasets: [
      {
        label: datasetLabel,
        data: values,
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "60%",
    plugins: {
      legend: {
        position: "right",
        labels: {
          padding: 14,
          font: { size: 12 },
          generateLabels: (chart) => {
            const d = chart.data;
            if (!d.labels?.length || !d.datasets?.length) return [];
            return d.labels.map((label, i) => {
              const pct = total > 0 ? Math.round((Number(values[i]) / total) * 100) : 0;
              const bgColor = colors[i] || "#ccc";
              return {
                text: formatInsightMonthYearSlash(label),
                fillStyle: bgColor,
                strokeStyle: "#fff",
                lineWidth: 1,
                hidden: !chart.getDataVisibility(i),
                index: i,
              };
            });
          },
        },
      },
      tooltip: {
        callbacks: {
          title: (items) => items.length ? formatInsightMonthYearSlash(String(items[0].label ?? "")) : "",
          label: (ctx) => {
            const pct = total > 0 ? Math.round((Number(ctx.raw) / total) * 100) : 0;
            return ` ${pct}%  (${Number(ctx.raw).toLocaleString("en-IN", { maximumFractionDigits: 0 })})`;
          },
        },
      },
      datalabels: {
        display: true,
        color: "#fff",
        font: { weight: "bold", size: 13 },
        formatter: (value) => {
          const pct = total > 0 ? Math.round((Number(value) / total) * 100) : 0;
          return pct > 5 ? `${pct}%` : "";
        },
      },
    },
  };
  return <Doughnut options={options} data={data} />;
}

/**
 * Line chart with two lines: MTD and LMTD.
 */
const mtdDatasetStyle = {
  fill: true,
  lineTension: 0.2,
  backgroundColor: "rgba(59, 79, 200, 0.2)",
  borderColor: INSIGHTS_MTD_COLOR,
  pointBorderColor: INSIGHTS_MTD_COLOR,
  pointBackgroundColor: "#fff",
  pointBorderWidth: 1,
  pointRadius: 3,
  pointHitRadius: 10,
};

const lmtdDatasetStyle = {
  fill: true,
  lineTension: 0.2,
  backgroundColor: "rgba(248, 113, 113, 0.2)",
  borderColor: INSIGHTS_LMTD_COLOR,
  pointBorderColor: INSIGHTS_LMTD_COLOR,
  pointBackgroundColor: "#fff",
  pointBorderWidth: 1,
  pointRadius: 3,
  pointHitRadius: 10,
};

export function InsightsMtdLmtdLineChart({
  labels = [],
  mtdValues = [],
  lmtdValues = [],
  emphasis = "MTD",
}) {
  if (!labels.length) return null;
  const mtdDimmed = emphasis === "LMTD";
  const lmtdDimmed = emphasis === "MTD";
  const data = {
    labels,
    datasets: [
      {
        label: "MTD",
        ...mtdDatasetStyle,
        data: mtdValues,
        borderWidth: mtdDimmed ? 1 : 2,
        borderColor: mtdDimmed
          ? "rgba(59, 79, 200, 0.35)"
          : INSIGHTS_MTD_COLOR,
        backgroundColor: mtdDimmed
          ? "rgba(59, 79, 200, 0.08)"
          : "rgba(59, 79, 200, 0.2)",
        pointBorderColor: mtdDimmed
          ? "rgba(59, 79, 200, 0.5)"
          : INSIGHTS_MTD_COLOR,
        pointRadius: mtdDimmed ? 2 : 3,
      },
      {
        label: "LMTD",
        ...lmtdDatasetStyle,
        data: lmtdValues,
        borderWidth: lmtdDimmed ? 1 : 2,
        borderColor: lmtdDimmed
          ? "rgba(248, 113, 113, 0.35)"
          : INSIGHTS_LMTD_COLOR,
        backgroundColor: lmtdDimmed
          ? "rgba(248, 113, 113, 0.08)"
          : "rgba(248, 113, 113, 0.2)",
        pointBorderColor: lmtdDimmed
          ? "rgba(248, 113, 113, 0.5)"
          : INSIGHTS_LMTD_COLOR,
        pointRadius: lmtdDimmed ? 2 : 3,
      },
    ],
  };
  const legendColorReference = [
    {
      text: "MTD",
      fillStyle: mtdDatasetStyle.backgroundColor,
      strokeStyle: mtdDatasetStyle.borderColor,
    },
    {
      text: "LMTD",
      fillStyle: lmtdDatasetStyle.backgroundColor,
      strokeStyle: lmtdDatasetStyle.borderColor,
    },
  ];

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        onClick: null,
        labels: {
          generateLabels(chart) {
            const colorOpt = chart.legend.options.labels.color;
            const fontColor =
              typeof colorOpt === "function"
                ? colorOpt({ chart })
                : (colorOpt ?? chart.options.color ?? "#666");
            return legendColorReference.map((ref, i) => ({
              text: ref.text,
              fillStyle: ref.fillStyle,
              strokeStyle: ref.strokeStyle,
              fontColor,
              hidden: false,
              lineWidth: 1,
              lineDash: [],
              datasetIndex: i,
            }));
          },
        },
      },
      datalabels: { display: false },
      title: { display: false },
    },
    scales: {
      x: {
        ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
        grid: { display: false },
        title: { display: true, text: "Day of Month", font: { size: 11 }, color: "#94a3b8" },
      },
      y: {
        beginAtZero: true,
        ticks: {
          beginAtZero: true,
          callback: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v,
        },
      },
    },
  };
  return <Line options={options} data={data} />;
}
